import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useToken } from "../context/TokenContext";
import { apiFetch } from "../utils/api";
import Panel from "./Panel";

const NAV = [
  { to: "/",             label: "📈 Syrinix",      end: true },
  { to: "/pok",          label: "⚙️ POK" },
  { to: "/gestion",      label: "🛠️ Gestion" },
  { to: "/visionnement", label: "👁️ Visionnement" },
  { to: "/mee",          label: "💧 Mises en Eau" },
  { to: "/admin",        label: "🛡️ Admin" },
  { to: "/ia",           label: "🤖 IA" },
];

function ActivityDot() {
  const [status, setStatus] = useState({ busy: false, tasks: [] });

  useEffect(() => {
    const poll = async () => {
      try {
        const data = await apiFetch("/api/sync/background-tasks");
        setStatus(data);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  const tooltip = status.busy
    ? status.tasks.join("\n")
    : "Aucune tâche en cours";

  return (
    <span title={tooltip} className="flex items-center justify-center w-6 h-6">
      <span
        className={`block w-2.5 h-2.5 rounded-full transition-colors ${
          status.busy
            ? "bg-green-500 animate-pulse"
            : "bg-gray-300"
        }`}
      />
    </span>
  );
}

function SplitIcon({ active }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1"  y="1" width="7" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="10" y="1" width="7" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export default function Layout({ children }) {
  const { expired, alert } = useToken();
  const location = useLocation();
  const [split, setSplit] = useState(() => localStorage.getItem("v3_split") === "true");

  // La vue divisée n'a pas de sens pour un rapport de triage : ce sont des pages de rapport
  // autonomes, toujours ouvertes dans un nouvel onglet dédié (Traitement.jsx::handleTriage pour
  // Syrinix, PokEssais.jsx pour POK), pas un des panneaux du mode divisé (Panel.jsx). Si la vue
  // divisée était déjà activée depuis un autre onglet (préférence partagée via localStorage), le
  // nouvel onglet affichait les panneaux par défaut au lieu du rapport demandé — sans la moindre
  // erreur, puisque c'est le comportement voulu du mode divisé, juste appliqué à une page qui n'y
  // a pas sa place (bug signalé par Jordan, 30/07/2026 — même raisonnement étendu au rapport de
  // triage POK, 23/09/2026, pour ne pas réintroduire le même bug).
  const STANDALONE_REPORT_PATHS = ["/rapport-triage", "/rapport-triage-pok"];
  const splitActive = split && !STANDALONE_REPORT_PATHS.includes(location.pathname);

  const toggleSplit = () => {
    setSplit(prev => {
      const next = !prev;
      localStorage.setItem("v3_split", String(next));
      return next;
    });
  };

  return (
    <div className={`bg-gray-100 ${splitActive ? "h-screen flex flex-col" : "min-h-screen"}`}>
      {/* Topbar */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-40 shrink-0">
        <div className="max-w-screen-2xl mx-auto px-4 h-14 flex items-center gap-4">
          {/* Logo / badge version */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="bg-violet-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              ⚡ v3 beta
            </span>
            <span className="text-gray-700 font-semibold text-sm hidden sm:block">Syrapi</span>
          </div>

          {/* Navigation (masquée en mode split — chaque panneau a sa propre nav) */}
          {!splitActive && (
            <nav className="flex items-center gap-1 overflow-x-auto flex-1">
              {NAV.map(({ to, label, end }) => {
                const isAdmin = to.includes("admin");
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) => {
                      const base = "px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors";
                      if (isAdmin && (expired || alert)) {
                        return `${base} ${isActive
                          ? "bg-red-700 text-white ring-2 ring-red-400 animate-pulse"
                          : "bg-red-100 text-red-700 ring-1 ring-red-300 animate-pulse"}`;
                      }
                      return `${base} ${isActive
                        ? "bg-violet-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"}`;
                    }}
                  >
                    {label}
                  </NavLink>
                );
              })}
            </nav>
          )}

          {splitActive && <div className="flex-1" />}

          {/* Actions droite */}
          <div className="flex items-center gap-3 shrink-0">
            <ActivityDot />
            <button
              onClick={toggleSplit}
              title={split ? "Vue simple" : "Vue divisée"}
              className={`p-1.5 rounded-lg transition-colors ${
                split
                  ? "bg-violet-100 text-violet-700 ring-1 ring-violet-300"
                  : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              }`}
            >
              <SplitIcon active={split} />
            </button>
          </div>
        </div>
      </header>

      {splitActive ? (
        /* Vue divisée : deux panneaux côte à côte, chacun scrollable indépendamment */
        <div className="flex gap-3 p-3 flex-1 min-h-0">
          <Panel defaultPage="traitement" />
          <div className="w-px bg-gray-300 shrink-0 self-stretch" />
          <Panel defaultPage="visionnement" />
        </div>
      ) : (
        /* Vue normale */
        <main className="max-w-screen-xl mx-auto px-4 py-6">
          {children}
        </main>
      )}
    </div>
  );
}
